import type { PatientResponse } from '../../api/generated';

// The rows of the add-patient search dropdown. The create row is present only
// when the user may create patients, so the list is the single place the
// PATIENT_WRITE gate is applied: no row means no keyboard target and no click
// target, rather than a gate repeated at each of them.
export type PatientSearchOption =
  | { kind: 'create' }
  | { kind: 'patient'; patient: PatientResponse };

const CREATE_OPTION_KEY = 'create';

// Rows are tracked by identity rather than by position because both inputs to
// the list can change while a row is highlighted — permissions when they are
// refetched, the patients when `setPatients` or a new assignment lands. A
// stored index would then point at a different row than the user aimed at, and
// Enter would act on it: assigning a patient they never selected.
export const getOptionKey = (option: PatientSearchOption): string =>
  option.kind === 'create' ? CREATE_OPTION_KEY : `patient:${option.patient.id}`;

export const buildPatientSearchOptions = (
  matches: PatientResponse[],
  canCreatePatient: boolean
): PatientSearchOption[] => {
  const createOption: PatientSearchOption[] = canCreatePatient ? [{ kind: 'create' }] : [];
  return [
    ...createOption,
    ...matches.map<PatientSearchOption>((patient) => ({ kind: 'patient', patient }))
  ];
};

// The key `delta` steps from the highlighted row, wrapping at both ends. A key
// that is no longer in `options` counts as nothing highlighted, so a row that
// has disappeared cannot drag the highlight along with it.
export const moveHighlight = (
  options: PatientSearchOption[],
  highlightedKey: string | null,
  delta: number
): string | null => {
  if (options.length === 0) return null;

  const current = options.findIndex((option) => getOptionKey(option) === highlightedKey);
  // Nothing highlighted, or a key whose row has since left the list: enter the
  // list from the end the user is moving towards.
  if (current < 0) {
    return getOptionKey(delta > 0 ? options[0] : options[options.length - 1]);
  }

  const next = (current + delta + options.length) % options.length;
  return getOptionKey(options[next]);
};

export const findHighlightedOption = (
  options: PatientSearchOption[],
  highlightedKey: string | null
): PatientSearchOption | null =>
  options.find((option) => getOptionKey(option) === highlightedKey) ?? null;
